import { useMemo, useState } from "react";
import styled from "@emotion/styled";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useSnackbarStore from "../../../../store/snackbar";

import { addNewPayroll, getPayrollList } from "../apiService";
// TODO: point these at your settings apiService
import { addNewSetting, getSettingByResource } from "../../apiService";

/* ------------------------------------------------------------------ */
/* Constants & helpers                                                 */
/* ------------------------------------------------------------------ */

const SETTINGS_RESOURCE = "payroll";
const SETTINGS_TAB = "general";

const RATES_KEY = ["payroll", "setup"];
const SETTINGS_KEY = ["settings", SETTINGS_RESOURCE, SETTINGS_TAB];

const PAY_TYPES = [
  { value: "hourly", label: "Hourly" },
  { value: "salary", label: "Salary" },
  { value: "commission", label: "Commission" },
  { value: "hourly_commission", label: "Hourly + Comm." },
];

const SCHEDULES = [
  { value: "weekly", label: "Weekly", perYear: 52 },
  { value: "biweekly", label: "Every 2 weeks", perYear: 26 },
  { value: "semimonthly", label: "Twice a month (1st & 16th)", perYear: 24 },
  { value: "monthly", label: "Monthly", perYear: 12 },
];

const hasHourly = (t) => t === "hourly" || t === "hourly_commission";
const hasCommission = (t) => t === "commission" || t === "hourly_commission";
const usesAnchor = (f) => f === "weekly" || f === "biweekly";

const todayStr = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local
const parseDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const fmtDate = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const num = (v) => (v === "" || v == null ? null : Number(v));

let tempKey = 0;
const newRow = () => ({
  key: `new-${++tempKey}`,
  pay_rate_id: null,
  staff_id: "",
  pay_type: "hourly",
  hourly_rate: "",
  annual_salary: "",
  commission_pct: "",
  effective_from: todayStr(),
});

/* API shape <-> form shape. Field names match the backend (staff_pay_rates columns). */

// `setting` is the settings row (or null if none saved yet); schedule lives in setting.data
const toForm = (data, setting) => ({
  frequency: setting?.data?.frequency ?? "biweekly",
  anchor_date: setting?.data?.anchor_date?.slice(0, 10) ?? "",
  rows: (data?.rates ?? []).map((r) => ({
    key: `id-${r.pay_rate_id}`,
    pay_rate_id: r.pay_rate_id,
    staff_id: String(r.staff_id), // select values are strings
    pay_type: r.pay_type,
    hourly_rate: r.hourly_rate ?? "",
    annual_salary: r.annual_salary ?? "",
    commission_pct: r.commission_pct ?? "",
    effective_from: r.effective_from?.slice(0, 10) ?? todayStr(),
  })),
});

const toScheduleData = (form) => ({
  frequency: form.frequency,
  anchor_date: usesAnchor(form.frequency) ? form.anchor_date : null,
});

// addNewSetting upserts by resource + resource_tab
const saveSchedule = (form) =>
  addNewSetting({
    resource: SETTINGS_RESOURCE,
    resource_tab: SETTINGS_TAB,
    settings_level: "system",
    data: toScheduleData(form),
  });

const toRatesPayload = (form) => ({
  rates: form.rows.map((r) => ({
    pay_rate_id: r.pay_rate_id,
    staff_id: Number(r.staff_id),
    pay_type: r.pay_type,
    hourly_rate: hasHourly(r.pay_type) ? num(r.hourly_rate) : null,
    annual_salary: r.pay_type === "salary" ? num(r.annual_salary) : null,
    commission_pct: hasCommission(r.pay_type) ? num(r.commission_pct) : null,
    effective_from: r.effective_from,
  })),
});

const rowErrors = (r) => {
  const e = {};
  if (!r.staff_id) e.staff_id = true;
  if (hasHourly(r.pay_type) && !(Number(r.hourly_rate) > 0)) e.hourly_rate = true;
  if (r.pay_type === "salary" && !(Number(r.annual_salary) > 0)) e.annual_salary = true;
  if (hasCommission(r.pay_type)) {
    const c = Number(r.commission_pct);
    if (r.commission_pct === "" || c < 0 || c > 100) e.commission_pct = true;
  }
  if (!r.effective_from) e.effective_from = true;
  return e;
};

/* Current + next pay period, for the preview box */
const getPeriods = (frequency, anchor_date) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (frequency === "monthly") {
    const y = now.getFullYear();
    const m = now.getMonth();
    return [0, 1].map((i) => [new Date(y, m + i, 1), new Date(y, m + i + 1, 0)]);
  }

  if (frequency === "semimonthly") {
    const y = now.getFullYear();
    const m = now.getMonth();
    const firstHalf = now.getDate() <= 15;
    const current = firstHalf
      ? [new Date(y, m, 1), new Date(y, m, 15)]
      : [new Date(y, m, 16), new Date(y, m + 1, 0)];
    const next = firstHalf
      ? [new Date(y, m, 16), new Date(y, m + 1, 0)]
      : [new Date(y, m + 1, 1), new Date(y, m + 1, 15)];
    return [current, next];
  }

  if (!anchor_date) return [];
  const len = frequency === "weekly" ? 7 : 14;
  let start = parseDate(anchor_date);
  while (addDays(start, len) <= now) start = addDays(start, len);
  return [0, 1].map((i) => {
    const s = addDays(start, i * len);
    return [s, addDays(s, len - 1)];
  });
};

/* ------------------------------------------------------------------ */
/* Components                                                          */
/* ------------------------------------------------------------------ */

const PayrollSetup = () => {
  const ratesQuery = useQuery({
    queryKey: RATES_KEY,
    queryFn: getPayrollList,
  });

  // getSettingByResource returns null when nothing is saved yet -> form uses defaults
  const settingQuery = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => getSettingByResource(SETTINGS_RESOURCE, SETTINGS_TAB),
  });

  if (ratesQuery.isLoading || settingQuery.isLoading) {
    return <Page><Muted>Loading payroll setup…</Muted></Page>;
  }
  if (ratesQuery.isError) return <Page><Muted>Couldn't load payroll setup.</Muted></Page>;

  // Remount the form whenever either query gets fresh data (e.g. after save)
  return (
    <SetupForm
      key={`${ratesQuery.dataUpdatedAt}-${settingQuery.dataUpdatedAt}`}
      data={ratesQuery.data}
      setting={settingQuery.data}
    />
  );
};

const SetupForm = ({ data, setting }) => {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore((s) => s.showSnackbar);

  const staff = data?.staff ?? [];
  const initial = useMemo(() => toForm(data, setting), [data, setting]);
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);

  const scheduleChanged =
    initial.frequency !== form.frequency || initial.anchor_date !== form.anchor_date;
  const ratesChanged = JSON.stringify(initial.rows) !== JSON.stringify(form.rows);

  // Only call the endpoints whose data actually changed
  const mutation = useMutation({
    mutationFn: ({ scheduleDirty, ratesDirty, values }) =>
      Promise.all([
        scheduleDirty && saveSchedule(values),
        ratesDirty && addNewPayroll(toRatesPayload(values)),
      ]),
    onSuccess: (_res, { scheduleDirty, ratesDirty }) => {
      showSnackbar("Payroll setup saved", "success");
      if (scheduleDirty) queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      if (ratesDirty) queryClient.invalidateQueries({ queryKey: RATES_KEY });
    },
    onError: (err) => {
      showSnackbar(err?.response?.data?.message || "Failed to save payroll setup", "error");
    },
  });

  /* Derived state */
  const errors = form.rows.map(rowErrors);
  const scheduleError = usesAnchor(form.frequency) && !form.anchor_date;
  const isValid = !scheduleError && errors.every((e) => Object.keys(e).length === 0);
  const perYear = SCHEDULES.find((s) => s.value === form.frequency)?.perYear ?? 26;
  const periods = getPeriods(form.frequency, form.anchor_date);
  const takenIds = form.rows.map((r) => String(r.staff_id)).filter(Boolean);
  const allAdded = staff.length > 0 && form.rows.length >= staff.length;

  const changeCount = useMemo(() => {
    const before = new Map(initial.rows.map((r) => [r.key, JSON.stringify(r)]));
    let n = 0;
    form.rows.forEach((r) => { if (before.get(r.key) !== JSON.stringify(r)) n++; });
    initial.rows.forEach((r) => { if (!form.rows.some((x) => x.key === r.key)) n++; });
    if (scheduleChanged) n++;
    return n;
  }, [form, initial, scheduleChanged]);

  /* Handlers */
  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const updateRow = (key, patch) =>
    setForm((f) => ({ ...f, rows: f.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const addRow = () => setForm((f) => ({ ...f, rows: [...f.rows, newRow()] }));

  const removeRow = (key) => setForm((f) => ({ ...f, rows: f.rows.filter((r) => r.key !== key) }));

  const discard = () => {
    setForm(initial);
    setSubmitted(false);
  };

  const save = () => {
    setSubmitted(true);
    if (!isValid) {
      showSnackbar("Please fix the highlighted fields", "error");
      return;
    }
    mutation.mutate({ scheduleDirty: scheduleChanged, ratesDirty: ratesChanged, values: form });
  };

  const showErr = (cond) => (submitted && cond ? "true" : "false");

  return (
    <Page>
      <Header>
        <Title>Payroll Setup</Title>
        <Muted>Set your pay schedule and how each staff member is paid.</Muted>
      </Header>

      {/* Salon-level schedule */}
      <Card as="section">
        <ScheduleRow>
          <ScheduleIntro>
            <SectionTitle>Pay schedule</SectionTitle>
            <Muted>Applies to everyone on payroll.</Muted>
          </ScheduleIntro>

          <ScheduleField>
            <Label htmlFor="pay-frequency">Frequency</Label>
            <Select
              id="pay-frequency"
              value={form.frequency}
              onChange={(e) => setField("frequency", e.target.value)}
            >
              {SCHEDULES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </ScheduleField>

          {usesAnchor(form.frequency) && (
            <ScheduleField>
              <Label htmlFor="pay-anchor">First period starts</Label>
              <Input
                id="pay-anchor"
                type="date"
                value={form.anchor_date}
                aria-invalid={showErr(scheduleError)}
                onChange={(e) => setField("anchor_date", e.target.value)}
              />
            </ScheduleField>
          )}

          <Preview>
            {periods.length ? (
              <>
                Current period: <b>{fmtDate(periods[0][0])} – {fmtDate(periods[0][1])}</b>
                <br />
                Next period: <b>{fmtDate(periods[1][0])} – {fmtDate(periods[1][1])}</b>
              </>
            ) : (
              "Pick a start date to see your periods."
            )}
          </Preview>
        </ScheduleRow>
      </Card>

      {/* Staff rates */}
      <ListHead>
        <SectionTitle>Staff pay rates</SectionTitle>
        <Button type="button" onClick={addRow} disabled={allAdded}>＋ Add staff member</Button>
      </ListHead>

      <Cards>
        {form.rows.map((row, i) => {
          const err = errors[i];
          return (
            <RateCard key={row.key}>
              <FieldStaff>
                <Label>Staff member</Label>
                <Select
                  value={row.staff_id}
                  aria-invalid={showErr(err.staff_id)}
                  onChange={(e) => updateRow(row.key, { staff_id: e.target.value })}
                >
                  <option value="">Select staff…</option>
                  {staff.map((s) => {
                    const taken = takenIds.includes(String(s.user_id)) && String(s.user_id) !== String(row.staff_id);
                    return (
                      <option key={s.user_id} value={s.user_id} disabled={taken}>
                        {s.name}{taken ? " (added)" : ""}
                      </option>
                    );
                  })}
                </Select>
              </FieldStaff>

              <div>
                <Label>Pay type</Label>
                <Pills role="group" aria-label="Pay type">
                  {PAY_TYPES.map((t) => (
                    <Pill
                      key={t.value}
                      type="button"
                      aria-pressed={row.pay_type === t.value}
                      onClick={() => updateRow(row.key, { pay_type: t.value })}
                    >
                      {t.label}
                    </Pill>
                  ))}
                </Pills>
              </div>

              {hasHourly(row.pay_type) && (
                <FieldRate>
                  <Label>Hourly rate</Label>
                  <MoneyInput
                    prefix="$"
                    suffix="/hr"
                    value={row.hourly_rate}
                    invalid={showErr(err.hourly_rate)}
                    onChange={(v) => updateRow(row.key, { hourly_rate: v })}
                  />
                </FieldRate>
              )}

              {row.pay_type === "salary" && (
                <FieldRate wide>
                  <Label>
                    Annual salary
                    {Number(row.annual_salary) > 0 && (
                      <Accent>
                        {" · $"}
                        {(row.annual_salary / perYear).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                        /check
                      </Accent>
                    )}
                  </Label>
                  <MoneyInput
                    prefix="$"
                    suffix="/yr"
                    value={row.annual_salary}
                    invalid={showErr(err.annual_salary)}
                    onChange={(v) => updateRow(row.key, { annual_salary: v })}
                  />
                </FieldRate>
              )}

              {hasCommission(row.pay_type) && (
                <FieldRate>
                  <Label>Commission</Label>
                  <MoneyInput
                    suffix="%"
                    max="100"
                    value={row.commission_pct}
                    invalid={showErr(err.commission_pct)}
                    onChange={(v) => updateRow(row.key, { commission_pct: v })}
                  />
                </FieldRate>
              )}

              <FieldEffective>
                <Label>Effective from</Label>
                <Input
                  type="date"
                  value={row.effective_from}
                  aria-invalid={showErr(err.effective_from)}
                  onChange={(e) => updateRow(row.key, { effective_from: e.target.value })}
                />
              </FieldEffective>

              <FieldDelete>
                <IconButton type="button" title="Remove" onClick={() => removeRow(row.key)}>✕</IconButton>
              </FieldDelete>
            </RateCard>
          );
        })}

        <AddTile type="button" onClick={addRow} disabled={allAdded}>
          ＋ Add staff member
        </AddTile>
      </Cards>

      {changeCount > 0 && (
        <SaveBar>
          <SaveBarInner>
            <span><Chip>{changeCount} change{changeCount === 1 ? "" : "s"}</Chip>&nbsp; Unsaved changes</span>
            <SaveActions>
              <Button type="button" variant="outline" onClick={discard} disabled={mutation.isPending}>
                Discard
              </Button>
              <Button type="button" onClick={save} disabled={mutation.isPending}>
                {mutation.isPending ? "Saving…" : "Save"}
              </Button>
            </SaveActions>
          </SaveBarInner>
        </SaveBar>
      )}
    </Page>
  );
};

const MoneyInput = ({ prefix, suffix, value, onChange, invalid, max }) => (
  <MoneyWrap hasPrefix={!!prefix}>
    {prefix && <Affix side="left">{prefix}</Affix>}
    <Input
      type="number"
      inputMode="decimal"
      min="0"
      max={max}
      step="0.01"
      placeholder="0.00"
      value={value}
      aria-invalid={invalid}
      onChange={(e) => onChange(e.target.value)}
    />
    <Affix side="right">{suffix}</Affix>
  </MoneyWrap>
);

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const c = {
  bg: "#f4f5f7",
  surface: "#ffffff",
  text: "#1f2430",
  muted: "#6b7280",
  border: "#e3e6eb",
  primary: "#6d3fc0",
  primarySoft: "#f1ebfb",
  danger: "#c0392b",
  dangerSoft: "#fdecea",
};

const mobile = "@media (max-width: 760px)";

const Page = styled.div`
  /* max-width: 120rem; */
  margin: 0 auto;
  padding: 2.4rem 1.6rem 10rem;
  color: ${c.text};
  font-size: 1.5rem;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  font-weight: 500;
  margin: 0 0 0.4rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.7rem;
  font-weight: 500;
  margin: 0 0 0.2rem;
`;

const Muted = styled.p`
  color: ${c.muted};
  font-size: 1.4rem;
  margin: 0;
`;

const Card = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: 1rem;
`;

const ScheduleRow = styled.div`
  padding: 1.6rem;
  display: flex;
  gap: 2.4rem;
  flex-wrap: wrap;
  align-items: flex-end;
`;

const ScheduleIntro = styled.div`
  flex: 1 1 22rem;
`;

const ScheduleField = styled.div`
  flex: 0 1 24rem;
  min-width: 17rem;
`;

const Preview = styled.div`
  flex: 1 1 28rem;
  font-size: 1.4rem;
  line-height: 1.5;
  color: ${c.muted};
  background: ${c.bg};
  border-radius: 0.6rem;
  padding: 0.8rem 1.2rem;

  b {
    color: ${c.text};
    font-weight: 500;
  }
`;

const ListHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
  margin: 2.4rem 0 1.2rem;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RateCard = styled(Card)`
  padding: 1.6rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1.6rem;
  align-items: flex-end;

  &:hover {
    border-color: #c9b8ea;
  }

  ${mobile} {
    position: relative;
    padding-right: 5.6rem;
  }
`;

const FieldStaff = styled.div`
  flex: 0 0 22rem;
  ${mobile} { flex: 1 1 100%; }
`;

const FieldRate = styled.div`
  flex: 0 0 ${(p) => (p.wide ? "17rem" : "14.5rem")};
  ${mobile} { flex: 1 1 13rem; }
`;

const FieldEffective = styled.div`
  flex: 0 0 16.5rem;
  margin-left: auto;
  ${mobile} { flex: 1 1 100%; margin-left: 0; }
`;

const FieldDelete = styled.div`
  ${mobile} {
    position: absolute;
    top: 1rem;
    right: 0.8rem;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 1.3rem;
  color: ${c.muted};
  margin-bottom: 0.5rem;
  white-space: nowrap;
`;

const Accent = styled.span`
  color: ${c.primary};
`;

const fieldCss = `
  width: 100%;
  height: 4rem;
  padding: 0 1.2rem;
  border: 1px solid ${c.border};
  border-radius: 0.6rem;
  background: ${c.surface};
  color: ${c.text};
  font: inherit;
  font-size: 1.5rem;

  &:focus {
    outline: 2px solid ${c.primarySoft};
    border-color: ${c.primary};
  }

  &[aria-invalid="true"] {
    border-color: ${c.danger};
    background: ${c.dangerSoft};
  }
`;

const Input = styled.input`${fieldCss}`;
const Select = styled.select`${fieldCss}`;

const MoneyWrap = styled.div`
  position: relative;

  input {
    padding-left: ${(p) => (p.hasPrefix ? "2.4rem" : "1.2rem")};
    padding-right: 4.4rem;
  }
`;

const Affix = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.side === "left" ? "left: 1.2rem;" : "right: 1.2rem;")}
  color: ${c.muted};
  font-size: 1.4rem;
  pointer-events: none;
`;

const Pills = styled.div`
  display: inline-flex;
  height: 4rem;
  border: 1px solid ${c.border};
  border-radius: 0.6rem;
  overflow: hidden;

  ${mobile} {
    display: flex;
    width: 100%;
  }
`;

const Pill = styled.button`
  border: 0;
  border-right: 1px solid ${c.border};
  background: ${c.surface};
  color: ${c.muted};
  padding: 0 1.4rem;
  font: inherit;
  font-size: 1.4rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  &:last-of-type { border-right: 0; }

  &[aria-pressed="true"] {
    background: ${c.primary};
    color: #fff;
  }

  ${mobile} {
    flex: 1;
    padding: 0 0.6rem;
    font-size: 1.3rem;
  }
`;

const Button = styled.button`
  border: ${(p) => (p.variant === "outline" ? `1px solid ${c.primary}` : 0)};
  background: ${(p) => (p.variant === "outline" ? "transparent" : c.primary)};
  color: ${(p) => (p.variant === "outline" ? c.primary : "#fff")};
  border-radius: 0.6rem;
  padding: 1rem 1.8rem;
  font: inherit;
  font-size: 1.4rem;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  background: none;
  border: 0;
  color: ${c.muted};
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  font-size: 2rem;
  cursor: pointer;

  &:hover {
    background: ${c.dangerSoft};
    color: ${c.danger};
  }
`;

const AddTile = styled.button`
  width: 100%;
  padding: 1.6rem;
  border: 1.5px dashed ${c.border};
  border-radius: 1rem;
  background: none;
  color: ${c.primary};
  font: inherit;
  font-size: 1.5rem;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${c.primarySoft};
    border-color: ${c.primary};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const SaveBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: ${c.surface};
  border-top: 1px solid ${c.border};
  padding: 1.2rem 1.6rem;
  display: flex;
  justify-content: center;
  font-size: 1.4rem;
`;

const SaveBarInner = styled.div`
  max-width: 120rem;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
`;

const SaveActions = styled.div`
  display: flex;
  gap: 0.8rem;
`;

const Chip = styled.span`
  display: inline-block;
  font-size: 1.2rem;
  font-weight: 500;
  padding: 0.2rem 0.8rem;
  border-radius: 1rem;
  background: ${c.primarySoft};
  color: ${c.primary};
`;

export default PayrollSetup;